<template>
  <v-card>
    <!-- Toolbar -->
    <v-toolbar density="comfortable" flat>
      <v-toolbar-title>
        Venta #{{ id }}
      </v-toolbar-title>

      <v-spacer />

      <!-- Menú de acciones (3 puntitos) -->
      <v-menu v-if="actions.length" location="bottom end">
        <template #activator="{ props }">
          <v-btn v-bind="props" icon variant="text">
            <v-icon>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            v-for="a in actions"
            :key="a.onClick"
            @click="onAction(a)"
          >
            <template #prepend>
              <v-icon v-if="a.icon" class="mr-2" size="small">{{ a.icon }}</v-icon>
            </template>
            <v-list-item-title>{{ a.label }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-btn icon variant="text" @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>

    <v-divider />

    <v-card-text class="py-4">
      <!-- Loading -->
      <div v-if="loading">
        <v-skeleton-loader type="article, table" />
      </div>

      <!-- Contenido -->
      <div v-else>
        <!-- Resumen -->
        <v-row dense class="mb-2">
          <v-col cols="12" md="7">
            <v-row dense>
              <v-col cols="12" class="pb-1">
                <div class="d-flex align-center">
                  <v-icon size="18" class="mr-2">mdi-domain</v-icon>
                  <strong>Cliente:&nbsp;</strong>
                  <span>{{ clientName(sale?.client) }}</span>
                </div>
              </v-col>
              <v-col cols="12" class="pb-1" v-if="sale?.campaign">
                <div class="d-flex align-center">
                  <v-icon size="18" class="mr-2">mdi-bullhorn</v-icon>
                  <strong>Campaña:&nbsp;</strong>
                  <span>{{ sale?.campaign }}</span>
                </div>
              </v-col>
              <v-col cols="12" class="pb-1">
                <div class="d-flex align-center">
                  <v-icon size="18" class="mr-2">mdi-account</v-icon>
                  <strong>Vendedor:&nbsp;</strong>
                  <span>{{ userFullName(sale?.user) }}</span>
                </div>
              </v-col>
              <v-col cols="12" class="pb-1">
                <div class="d-flex align-center">
                  <v-icon size="18" class="mr-2">mdi-calendar</v-icon>
                  <strong>Creación:&nbsp;</strong>
                  <span>{{ formatDate(sale?.created_at) }}</span>
                </div>
              </v-col>
              <v-col cols="12" class="pb-1" v-if="!!sale?.needs_authorization">
                <v-chip color="orange" size="small" label>
                  Requiere Autorización
                </v-chip>
              </v-col>
            </v-row>
          </v-col>

          <v-col cols="12" md="5">
            <v-card variant="tonal">
              <v-card-text class="py-3">
                <div class="d-flex justify-space-between align-center mb-1">
                  <div class="text-caption">Subtotal</div>
                  <div class="text-subtitle-2">{{ fmtCurrency(sale?.subtotal) }}</div>
                </div>
                <div class="d-flex justify-space-between align-center mb-1">
                  <div class="text-caption">IVA</div>
                  <div class="text-subtitle-2">{{ fmtCurrency(sale?.iva) }}</div>
                </div>
                <v-divider class="my-2" />
                <div class="d-flex justify-space-between align-center">
                  <div class="text-subtitle-2"><strong>Total</strong></div>
                  <div class="text-subtitle-1"><strong>{{ fmtCurrency(sale?.total) }}</strong></div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Items -->
        <v-card class="mt-3" variant="elevated">
          <v-card-title class="py-3">Productos</v-card-title>
          <v-divider />
          <v-data-table
            :headers="itemHeaders"
            :items="items"
            density="comfortable"
            :items-per-page="10"
          >
            <template #item.product="{ item }">
              {{ item?.sold_item?.attributes?.description || item?.description || '—' }}
            </template>
            <template #item.quantity="{ item }">
              {{ item?.quantity ?? '—' }}
            </template>
            <template #item.unit_price="{ item }">
              {{ fmtCurrency(item?.unit_price) }}
            </template>
            <template #item.price_with_iva="{ item }">
              {{ fmtCurrency(item?.price_with_iva) }}
            </template>
            <template #item.needs_authorization="{ item }">
              <v-chip v-if="item?.needs_authorization" color="orange" size="x-small" label>Autorizar</v-chip>
            </template>
          </v-data-table>
        </v-card>

        <!-- Archivos / PDF (opcional) -->
        <v-row dense class="mt-3">
          <v-col cols="12" md="6" v-if="sale?.pdf_document">
            <v-card variant="tonal">
              <v-card-text>
                <v-btn :href="toUrl(sale?.pdf_document)" target="_blank" rel="noopener" prepend-icon="mdi-open-in-new" variant="text">
                  Ver PDF de la venta
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="6" v-if="sale?.quotation">
            <v-card variant="tonal">
              <v-card-text>
                <div class="d-flex align-center">
                  <v-icon class="mr-2">mdi-file-document</v-icon>
                  <div>
                    <div class="text-subtitle-2">Cotización #{{ sale.quotation.id }}</div>
                    <div class="text-caption">Campaña: {{ sale.quotation.campaign || '—' }}</div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { ref, onMounted, getCurrentInstance, computed } from 'vue';
import { useSalesStore } from '@/store/sales';
import { useFloatFormStore } from '@/store/floatForm';
import { useBottomSheetStore } from '@/store/bottomSheet';
import { useRoute } from 'vue-router';
import { loadModuleConfig, resolveViewName, getListPreset } from '@/lib/presets';

const props = withDefaults(defineProps<{ id: number; moduleName?: string; title?: any; rowActionHandlers?: Record<string, Function> }>(), {
  moduleName: 'Sales',
});

const emit = defineEmits<{ (e:'close'): void; (e:'refresh'): void }>();

const store = useSalesStore();
const floatFormStore = useFloatFormStore();
const bottomSheetStore = useBottomSheetStore();
const route = useRoute();

const sale = ref<any>(null);
const loading = ref(true);

// ====== helpers UI ======
const fmtCurrency = (v: any) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 }).format(Number(v || 0));
const formatDate = (v?: string) => (v ? new Date(v).toLocaleString('es-MX') : '—');
const clientName = (c: any) => c?.attributes?.commercial_name || c?.attributes?.legal_name || c?.attributes?.name || '—';
const userFullName = (u: any) => [u?.name, u?.last_name ?? u?.last].filter(Boolean).join(' ') || '—';
const toUrl = (v: any) => (typeof v === 'string' ? v : (v?.url || v?.path || v?.file));

// ====== tabla de items ======
const itemHeaders = [
  { title: 'Producto', value: 'product' },
  { title: 'Cantidad', value: 'quantity' },
  { title: 'Precio unitario', value: 'unit_price' },
  { title: 'Total', value: 'price_with_iva' },
  { title: 'Autorización', value: 'needs_authorization' },
];
const items = computed(() => sale.value?.sale_items || []);

// ====== acciones (mismo origen que la lista) ======
type ActionMeta = {
  label: string;
  onClick: string;
  icon?: string;
  permission?: string;
  kind?: 'default'|'extra';
  showIf?: (item:any)=>boolean;
  showWhen?: string;
};

const actions = ref<ActionMeta[]>([]);
const instance = getCurrentInstance();
const $can = instance?.appContext.config.globalProperties.$can as (perm: string, item?: any) => boolean;

function byPath(obj:any, path?:string){
  if (!obj || !path) return undefined;
  return path.split('.').reduce((a,k)=> (a ? a[k] : undefined), obj);
}
function passesShow(a:any, item:any){
  if (typeof a?.showIf === 'function') return !!a.showIf(item);
  if (a?.showWhen) return !!byPath(item, a.showWhen);
  return true;
}

onMounted(async () => {
  try {
    loading.value = true;
    sale.value = await store.fetchSaleById(props.id);

    // carga preset de acciones
    const mod = await loadModuleConfig(props.moduleName);
    const view = resolveViewName(props.moduleName, route, mod);
    const preset = getListPreset(mod, view);
    const raw = (preset.rowActions || (mod as any).ROW_ACTIONS || []) as ActionMeta[];

    actions.value = raw
      .filter(a => !a.permission || ($can && $can(a.permission, sale.value)))
      .filter(a => passesShow(a, sale.value));
  } catch (e) {
    console.error('[Sales Detail] error:', e);
  } finally {
    loading.value = false;
  }
});

// Handlers coherentes con lista
function editItem() {
  floatFormStore.openForm({
    component: 'EditForm',
    formTitle: 'Editar Venta',
    formWidth: '1080',
    module: props.moduleName,
    props: {
      item: sale.value,
      onUpdate: async (data:any) => {
        await store.updateSale(data.id, data);
        emit('refresh');
      },
    },
  });
}
function deleteItem() {
  bottomSheetStore.openSheet({
    module: props.moduleName,
    formTitle: `¿Seguro(a) que deseas eliminar Venta ${sale.value?.id}?`,
    props: {
      item: sale.value,
      message: 'Esta acción no se puede deshacer.',
      action: async () => {
        await store.deleteSale(sale.value?.id);
        emit('refresh');
        emit('close');
      },
    },
  });
}

async function onAction(a: ActionMeta) {
  if (!sale.value) return;

  if (a.onClick === '__edit') return editItem();
  if (a.onClick === '__delete') return deleteItem();

  // custom desde Sales.vue (authorizeSale, confirmSale, etc.)
  const handlers = props.rowActionHandlers || {};
  if (handlers[a.onClick]) {
    await handlers[a.onClick](sale.value);
    emit('refresh');
    return;
  }

  console.warn('No handler for action:', a.onClick);
}
</script>

<style scoped>
.text-caption {
  font-size: 0.75rem;
  font-weight: 500;
}
</style>
